import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icons,
  iconSize,
  markupInks,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  type LucideIcon,
} from '@munifocus/ui';
import {
  MARKUP_COLOURS,
  MAX_MARKUP_ANNOTATIONS,
  MAX_MARKUP_POINTS,
  MAX_MARKUP_TEXT,
  type Evidence,
  type MarkupAnnotation,
  type MarkupColour,
  type MarkupShape,
} from '@munifocus/contracts-gov';
import { drawAnnotations, normalisePoint } from './markup-draw';

const TOOLS: Array<{ shape: MarkupShape; Icon: LucideIcon }> = [
  { shape: 'arrow', Icon: Icons.ArrowUpRight },
  { shape: 'ellipse', Icon: Icons.Circle },
  { shape: 'rect', Icon: Icons.Square },
  { shape: 'freehand', Icon: Icons.PenLine },
  { shape: 'text', Icon: Icons.Type },
  { shape: 'marker', Icon: Icons.MapPin },
];

/**
 * Draw on an evidence photo. The canvas is a preview over the photo; Save
 * sends only the shapes, and gov-api renders the marked-up copy from the
 * original's verified bytes. The original is never changed.
 */
export function MarkupEditor({
  photo,
  saving,
  onClose,
  onSave,
}: {
  photo: Evidence;
  saving: boolean;
  onClose: () => void;
  onSave: (annotations: MarkupAnnotation[], note: string) => void;
}) {
  const { t } = useTranslation();
  const [tool, setTool] = useState<MarkupShape>('arrow');
  const [colour, setColour] = useState<MarkupColour>('red');
  const [shapes, setShapes] = useState<MarkupAnnotation[]>([]);
  const [draft, setDraft] = useState<MarkupAnnotation | null>(null);
  const [callout, setCallout] = useState('');
  const [note, setNote] = useState('');
  const [size, setSize] = useState({ w: 0, h: 0 });
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const image = useRef<HTMLImageElement | null>(null);

  const measure = useCallback(() => {
    const img = image.current;
    if (img) setSize({ w: img.clientWidth, h: img.clientHeight });
  }, []);

  // Size the canvas to the photo as displayed, once it has loaded and on resize.
  useEffect(() => {
    const img = image.current;
    if (img?.complete) measure();
    img?.addEventListener('load', measure);
    window.addEventListener('resize', measure);
    return () => {
      img?.removeEventListener('load', measure);
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  useEffect(() => {
    const ctx = canvas.current?.getContext?.('2d');
    if (!ctx || !size.w) return;
    drawAnnotations(ctx, size.w, size.h, draft ? [...shapes, draft] : shapes);
  }, [shapes, draft, size]);

  const full = shapes.length >= MAX_MARKUP_ANNOTATIONS;
  const needsText = tool === 'text';
  const text = callout.trim().slice(0, MAX_MARKUP_TEXT);

  const point = (e: PointerEvent<HTMLCanvasElement>) =>
    normalisePoint(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect());

  const onDown = (e: PointerEvent<HTMLCanvasElement>) => {
    if (full) return;
    const p = point(e);
    if (tool === 'text' || tool === 'marker') {
      if (tool === 'text' && !text) return;
      setShapes((s) => [...s, { kind: tool, colour, points: [p], ...(text ? { text } : {}) }]);
      setCallout('');
      return;
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDraft({ kind: tool, colour, points: tool === 'freehand' ? [p] : [p, p] });
  };

  const onMove = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!draft) return;
    const p = point(e);
    if (draft.kind === 'freehand') {
      if (draft.points.length < MAX_MARKUP_POINTS) {
        setDraft({ ...draft, points: [...draft.points, p] });
      }
    } else {
      setDraft({ ...draft, points: [draft.points[0], p] });
    }
  };

  const onUp = () => {
    if (!draft) return;
    const [a, b] = [draft.points[0], draft.points[draft.points.length - 1]];
    const moved = Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) > 0.01;
    if (moved && (draft.kind !== 'freehand' || draft.points.length >= 2)) {
      setShapes((s) => [...s, draft]);
    }
    setDraft(null);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('gov.evidenceMarkup.editorTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('gov.evidenceMarkup.editorIntro')}
          </Typography>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ alignItems: { md: 'center' } }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={tool}
              onChange={(_, v: MarkupShape | null) => v && setTool(v)}
              aria-label={t('gov.evidenceMarkup.tools')}
            >
              {TOOLS.map(({ shape, Icon }) => {
                return (
                  <ToggleButton
                    key={shape}
                    value={shape}
                    aria-label={t(`gov.evidenceMarkup.shape.${shape}`)}
                  >
                    <Tooltip title={t(`gov.evidenceMarkup.shape.${shape}`)}>
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        <Icon size={iconSize.sm} />
                      </Box>
                    </Tooltip>
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={colour}
              onChange={(_, v: MarkupColour | null) => v && setColour(v)}
              aria-label={t('gov.evidenceMarkup.ink')}
            >
              {MARKUP_COLOURS.map((c) => (
                <ToggleButton key={c} value={c} aria-label={t(`gov.evidenceMarkup.colour.${c}`)}>
                  <Box
                    component="span"
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: markupInks[c],
                      border: 1,
                      borderColor: 'divider',
                    }}
                  />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Button
              size="small"
              startIcon={<Icons.Undo2 size={iconSize.sm} />}
              disabled={!shapes.length}
              onClick={() => setShapes((s) => s.slice(0, -1))}
            >
              {t('gov.evidenceMarkup.undo')}
            </Button>
          </Stack>
          {(tool === 'text' || tool === 'marker') && (
            <TextField
              size="small"
              label={t(
                needsText ? 'gov.evidenceMarkup.calloutText' : 'gov.evidenceMarkup.markerLabel',
              )}
              helperText={t(
                needsText ? 'gov.evidenceMarkup.calloutHint' : 'gov.evidenceMarkup.markerHint',
              )}
              value={callout}
              onChange={(e) => setCallout(e.target.value)}
              slotProps={{ htmlInput: { maxLength: MAX_MARKUP_TEXT } }}
            />
          )}
          <Box sx={{ position: 'relative', alignSelf: 'center', maxWidth: '100%', lineHeight: 0 }}>
            <Box
              component="img"
              ref={image}
              src={photo.downloadUrl}
              alt={photo.originalFilename}
              sx={{ maxWidth: '100%', maxHeight: '60vh', display: 'block', userSelect: 'none' }}
              draggable={false}
            />
            <Box
              component="canvas"
              ref={canvas}
              data-testid="markup-canvas"
              aria-label={t('gov.evidenceMarkup.canvasLabel')}
              width={size.w}
              height={size.h}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerLeave={onUp}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                cursor: 'crosshair',
                touchAction: 'none',
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" aria-live="polite">
            {t('gov.evidenceMarkup.shapeCount', { count: shapes.length })}
          </Typography>
          <TextField
            label={t('gov.evidenceMarkup.note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!shapes.length || saving}
          onClick={() => onSave(shapes, note)}
        >
          {t('gov.evidenceMarkup.saveCopy')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
